import * as React from "react";
import Dragula from "react-dragula";

import {cs}              from "@Common/react/chain-services";
import {fragments}       from "@Common/react/fragments";
import {Load2}           from "@Common/react/load2";
import {UseState}        from "@Common/react/use-state";
import {scopedState}     from "@Common/react/scoped-state";
import {PhotoDashboard}  from "@Common/ui-components/photo-dashboard/photo-dashboard";
import {SliderCheckbox}  from "@Common/ui-components/slider-checkbox/slider-checkbox";
import {faCheck, faLock, faPen, faUnlock, faXmark} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {consumeContext}   from "@Common/react/context";

import {Instruction}  from "../../../../customize/steps/common/instruction/instruction";

import {CcIntegration}          from "../cc-integration/cc-integration";
import {Controls}               from "../controls/controls";
import {templateConfigurations} from "../template-configurations/template-configurations";

export const TemplateCreationView = ({
    ccStateId, data1, data2, dataProperties, idmlPath, mockData, templateIds, apis, company, session, next
}) => cs(
    consumeContext("routerHistory"),
    ["userData", ({}, next) => next(
        !session.loading && company && {
            templateIds,
            ccStateId,
            sourceData: {
                xd: {
                    "user": {...session?.data?.agent},
                    "office": {...session?.data?.branch},
                    region: {...session?.data?.region},
                    company: {...company, imageGallery: {...company.imageGallery}},
                    listing: {},
                    messages: {...session?.data?.messages},
                    templateOptions: {options:[{devKey:"enable_cc_adv_mode", value: 1}]}
                },
            },
        }
    )],
    ["reload", (_, next) => UseState({ initValue: false, next })],
    ["cc", ({userData, reload}, next) => CcIntegration({
        userData,
        idmlPath,
        apis,
        dataProperties,
        reload: () => reload.onChange(!reload.value),
        next,
    })],// prepare loader for photo services
    ["editingStateId", ({cc}, next) => UseState({ initValue: cc.editingStateId, next })],
    ["photoServices", ({}, next) => Load2({
        fetch: () => apis.next.gallery.photoServices.getAvailableServices(),
        next,
    })],
    ["companyGallery", ({}, next) => Load2({
        fetch: () => apis.next.gallery.propertyPhotos.getFolders(),
        next,
    })],
    ["photoDashboard", ({}, next) => PhotoDashboard({next})],
    ({cc, photoDashboard}, next) => fragments(
        next(),
        cc.registerCustomGallery({
            onOpen: (modalData) => photoDashboard.show(modalData),
        }),
    ),
    ["updatingFieldSequence", ({cc}, next) => UseState({_key: cc.currentSide, next})],
    ({cc, companyGallery, photoServices, editingStateId, routerHistory, updatingFieldSequence}) => {
        if(cc.error) {
            routerHistory.replace(`/manage-templates/private#cc-error`);
        }
        return <> {next({
            ccConfig: cc.ccConfig,
            ccStateId: cc.ccStateId,
            items: cc.items,
            save: cc.save,
            saveEditingState: cc.saveEditingState,
            sides: cc.sides,
            editingStateId: cc.editingStateId,
            ccVersion: cc.ccVersion,
            render: () => {
                if( editingStateId.value != cc.editingStateId ) {
                    editingStateId.onChange(cc.editingStateId)
                }

                let mockDataFormatted;
                if(!mockData.loading && !mockDataFormatted){
                    mockDataFormatted = {'xd': {}};
                    mockData.value.forEach((data)=>{
                        mockDataFormatted['xd'][data.name] = data.value ? JSON.parse( data.value ) : {}
                    });
                    mockDataFormatted['xd']['company'] = {...company};
                }
                let drake = null;
                const dragulaDecorator = (componentBackingInstance) => {
                    if (componentBackingInstance && updatingFieldSequence.value) {
                        let options = { mirrorContainer: document.getElementById("dragulaContainer") };
                        drake = Dragula([componentBackingInstance], options);
                    }
                    else {
                        drake?.destroy();
                    }
                };

                return <>
                    {!cc.initialized && (
                        <div className="container">
                            <div className="overlay" />
                            <img className="loadingImage" {...{
                                src: "/app/assets/SPINNER.svg",
                                alt: "spinner"
                            }}/>
                        </div>
                    )}
                    <div className="main">
                        <div className="controls">
                            {Controls({cc})}
                        </div>
                        <div className="line2">
                            <div className="editor">
                                <div className={"editor-items-standard"}>
                                    <div className="content">
                                        <div className="exhibit-xpresso-9dd">
                                            {cc.render({heightPerWidthRatio: .7})}
                                            <div style={{marginTop: "5px", display: "flex"}}>
                                                <div className="infoDiv">
                                                    {Instruction({
                                                        text: "Click on the template to make edits directly",
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="details">
                                {cs(
                                    ["showAll", (_, next) => UseState({next})],
                                    ({showAll}) => <>
                                        <div className="behaviors">
                                            {!updatingFieldSequence.value ? <a xd-qa-type="link" xd-qa-name="Edit Sequence"
                                                onClick={async () => {
                                                    updatingFieldSequence.onChange(true);
                                                }}
                                            >
                                                Edit Sequence
                                            </a> : <a xd-qa-type="link" xd-qa-name="Edit Sequence"
                                                onClick={async() => {
                                                    let folderElements = document.getElementsByClassName("form");
                                                    let sequence = 1;
                                                    let data1Obj = scopedState(data1, [cc.currentSide]).value ?? {};
                                                    for( const element of folderElements ) {
                                                        let fieldName = element.getAttribute('id');
                                                        if( fieldName ) {
                                                            if( !data1Obj?.[fieldName] ) {
                                                                data1Obj[fieldName] = {}
                                                            }
                                                            data1Obj[fieldName]["index"] = sequence;
                                                            sequence++;
                                                        }
                                                    }
                                                    scopedState(data1, [cc.currentSide]).onChange(data1Obj);
                                                    updatingFieldSequence.onChange(false);
                                                }}
                                            >
                                                Save Sequence
                                            </a>}
                                            <div>Expand All</div>
                                            {SliderCheckbox({
                                                xdQaName: "showAll",
                                                value: showAll.value,
                                                onChange: () => showAll.onChange(!showAll.value)
                                            })}
                                        </div>
                                        <div className="form-fields" ref={dragulaDecorator}>
                                            {cc.items?.filter((obj) => (
                                                obj._parentContainer?.parentComponent?._name === cc.currentSide
                                            ))?.sort((a, b) => {
                                                let aValue = scopedState(data1, [cc.currentSide, a.name, "index"]).value
                                                let bValue = scopedState(data1, [cc.currentSide, b.name, "index"]).value
                                                if ( !aValue && !bValue ) {
                                                    return 0;
                                                } else if ( !aValue ) {
                                                    return -1;
                                                } else if ( !bValue ) {
                                                    return 1;
                                                } else {
                                                    return aValue - bValue;
                                                }
                                            })?.map((item, index) => {
                                                let label = typeof scopedState(data1, [item.parentContainer.parentComponent.name, item.name, "label"]).value === "string" ?
                                                scopedState(data1, [item.parentContainer.parentComponent.name, item.name, "label"]).value : item.name;
                                                return  <>{cs(
                                                    ["labelEditValue", (_, next) => UseState({_key: `${cc.currentSide}${updatingFieldSequence.value}`, initValue: label, next})],
                                                    ["showEdit", (_, next) => UseState({_key: `${cc.currentSide}${updatingFieldSequence.value}`, next})],
                                                    ["showInput", (_, next) => UseState({_key: `${cc.currentSide}${updatingFieldSequence.value}`, next})],
                                                    ({labelEditValue, showEdit, showInput}) => <div
                                                        className="form"
                                                        id={item.name}
                                                        key={`${item.name}-${index}`}
                                                        onClick={
                                                            //@ts-ignore
                                                            () => cc.selectField([{'name':item.name}])
                                                        }
                                                        onMouseEnter={() => showEdit.onChange(true)}
                                                        onMouseLeave={() => showEdit.onChange(false)}
                                                    >
                                                        <div className="form-group">
                                                            <div className="control-label">
                                                                {!showInput.value ? label : <input
                                                                    maxLength={30}
                                                                    type="text"
                                                                    value={labelEditValue.value}
                                                                    onInput={(e) => labelEditValue.onChange(e.currentTarget.value)}
                                                                    xd-qa-type="input"
                                                                    xd-qa-name="label"
                                                                />}
                                                                <div className="controls">
                                                                    {!!showInput.value ? <>
                                                                        <button
                                                                            aria-label="Confirm"
                                                                            className="confirm"
                                                                            xd-qa-type="button"
                                                                            xd-qa-name="Confirm Label Edit"
                                                                            onClick={async() => {
                                                                                scopedState(data1, [item.parentContainer.parentComponent.name, item.name, "label"]).onChange(labelEditValue.value)
                                                                                showInput.onChange(false);
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon icon={faCheck} />
                                                                        </button>
                                                                        <button
                                                                            aria-label="Cancel"
                                                                            className="cancel"
                                                                            xd-qa-type="button"
                                                                            xd-qa-name="Cancel Label Edit"
                                                                            onClick={async() => {
                                                                                labelEditValue.onChange(label);
                                                                                showInput.onChange(false);
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon icon={faXmark} />
                                                                        </button>
                                                                    </> : !!showEdit.value && <button
                                                                        aria-label="Edit Label"
                                                                        className="edit"
                                                                        xd-qa-type="button"
                                                                        xd-qa-name="Edit Label"
                                                                        onClick={async() => {
                                                                            showInput.onChange(true);
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faPen} />
                                                                    </button>}
                                                                    <button
                                                                        aria-label={item.locked ? "Locked" : "Unlocked"}
                                                                        className="lockController"
                                                                        onClick={async() => {
                                                                            try{
                                                                                let product = await cc.getProduct();
                                                                                let itemChanging = await product.getItemByName(item.name);
                                                                                itemChanging['locked'] = !item.locked;
                                                                                await product.setItem(itemChanging);
                                                                                await cc.reloadItems();
                                                                                scopedState(dataProperties, [item._parentContainer.parentComponent.name, item.name, 'locked']).onChange(!item.locked);
                                                                            }
                                                                            catch(error){
                                                                                console.log('Error:', error);
                                                                            }
                                                                        }}
                                                                        xd-qa-type="button"
                                                                        xd-qa-name="locked"
                                                                    >
                                                                        <FontAwesomeIcon
                                                                            icon={item.locked ? faLock : faUnlock}
                                                                            style={item.locked && {color: "red"}}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div className="content" style={
                                                                !updatingFieldSequence.value && ( !!showAll.value || cc.selectedItemName == item.name )
                                                                ? {display:"block"} : {display:"none"}
                                                            }>
                                                                {templateConfigurations(item, company, companyGallery, data1, data2, dataProperties,
                                                                    cc.getProduct, mockDataFormatted, photoServices, cc.reloadItems
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}</>
                                            })}
                                        </div>
                                        <div id="dragulaContainer"></div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </>;
            },
        })} </>
    }
);